import { useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { aiAPI, type AIProvider } from "../../lib/api";
import { useAIConfig } from "../../contexts/AIConfigContext";
import styles from "./InlineAISetup.module.css";

function UpgradeBanner() {
  const { id: orgId } = useParams<{ id: string }>();

  return (
    <div className={styles.upgradeBanner}>
      <LockIcon />
      <div>
        <p className={styles.upgradeTitle}>Enterprise Feature</p>
        <p className={styles.upgradeText}>
          Bring-your-own-key AI configuration is available on the Enterprise plan. Upgrade to use
          your own API keys for custom models, higher limits, or compliance requirements.
        </p>
        {orgId && (
          <Link to={`/organizations/${orgId}/billing`} className={styles.upgradeButton}>
            Upgrade to Enterprise →
          </Link>
        )}
      </div>
    </div>
  );
}

function LockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M5 7V5a3 3 0 0 1 6 0v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

const PROVIDERS: {
  value: AIProvider;
  label: string;
  icon: React.ReactNode;
  keyUrl?: string;
}[] = [
  {
    value: "anthropic",
    label: "Anthropic",
    icon: <AnthropicIcon />,
    keyUrl: "https://console.anthropic.com/settings/keys",
  },
  {
    value: "openai",
    label: "OpenAI",
    icon: <OpenAIIcon />,
    keyUrl: "https://platform.openai.com/api-keys",
  },
  {
    value: "custom",
    label: "Custom",
    icon: <CustomIcon />,
  },
];

const API_KEY_PATTERNS: Record<AIProvider, { regex: RegExp; hint: string }> = {
  anthropic: {
    regex: /^sk-ant-.+$/,
    hint: "Anthropic keys start with sk-ant-",
  },
  openai: {
    regex: /^sk-.+$/,
    hint: "OpenAI keys start with sk-",
  },
  custom: {
    regex: /^.+$/,
    hint: "Enter the API key for your custom endpoint",
  },
};

const PROVIDER_PLACEHOLDERS: Record<AIProvider, string> = {
  anthropic: "sk-ant-api03-...",
  openai: "sk-...",
  custom: "Optional — leave blank for local LLMs",
};

const MIN_KEY_LENGTHS: Record<AIProvider, number> = {
  anthropic: 10,
  openai: 10,
  custom: 1,
};

interface InlineAISetupProps {
  onConfigured?: () => void;
}

const DEFAULT_CUSTOM_BASE_URL = "http://localhost:11434/v1";

export function InlineAISetup({ onConfigured }: InlineAISetupProps) {
  const { refreshAIStatus, byokAllowed } = useAIConfig();

  if (!byokAllowed) {
    return <UpgradeBanner />;
  }
  const [provider, setProvider] = useState<AIProvider>("anthropic");
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [modelName, setModelName] = useState("");
  const [baseUrlError, setBaseUrlError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<"success" | "error" | null>(null);
  const [verified, setVerified] = useState(false);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [fetchingModels, setFetchingModels] = useState(false);
  const [fetchModelsError, setFetchModelsError] = useState<string | null>(null);

  const validateUrl = (url: string): string | null => {
    const trimmed = url.trim();
    if (!trimmed) return null;
    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return "URL must start with http:// or https://";
      }
      return null;
    } catch {
      return "Please enter a valid URL (e.g. http://localhost:11434/v1)";
    }
  };

  const validate = (key: string, p: AIProvider): string | null => {
    const trimmed = key.trim();
    if (!trimmed) return null;
    const pattern = API_KEY_PATTERNS[p];
    return pattern.regex.test(trimmed) ? null : pattern.hint;
  };

  const isFormatValid = (key: string, p: AIProvider): boolean => {
    const trimmed = key.trim();
    if (p === "custom") return true;
    if (!trimmed) return false;
    const pattern = API_KEY_PATTERNS[p];
    return pattern.regex.test(trimmed) && trimmed.length >= MIN_KEY_LENGTHS[p];
  };

  const handleFetchModels = useCallback(async () => {
    const trimmedUrl = baseUrl.trim();
    if (!trimmedUrl) return;
    const urlError = validateUrl(trimmedUrl);
    if (urlError) {
      setBaseUrlError(urlError);
      return;
    }
    setFetchingModels(true);
    setFetchModelsError(null);
    try {
      const result = await aiAPI.fetchModels(trimmedUrl, apiKey.trim() || undefined);
      if (result.ok && result.models.length > 0) {
        setAvailableModels(result.models);
        if (!modelName.trim()) {
          setModelName(result.models[0]);
        }
      } else if (result.ok && result.models.length === 0) {
        setFetchModelsError("No models found on this server.");
        setAvailableModels([]);
      } else {
        setFetchModelsError(result.error || "Failed to fetch models");
        setAvailableModels([]);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not reach the server.";
      setFetchModelsError(message);
      setAvailableModels([]);
    } finally {
      setFetchingModels(false);
    }
  }, [baseUrl, apiKey, modelName]);

  const fieldsValid =
    provider !== "custom"
      ? apiKey.trim() && isFormatValid(apiKey, provider)
      : baseUrl.trim() && modelName.trim() && !baseUrlError;

  const canTest = !saving && !testing && fieldsValid;
  const canSubmit = canTest && testResult !== "error";

  const classifyError = (
    errorMsg: string,
  ): { icon: React.ReactNode; category: string; suggestion: string } => {
    const lower = errorMsg.toLowerCase();
    if (lower.includes("authentication failed") || lower.includes("api key was rejected")) {
      return {
        icon: <KeyErrorIcon />,
        category: "Invalid API Key",
        suggestion: "Double-check your key and make sure it hasn't been revoked.",
      };
    }
    if (lower.includes("timed out") || lower.includes("timeout")) {
      return {
        icon: <TimeoutIcon />,
        category: "Connection Timeout",
        suggestion: "The server didn't respond in time. Check that it's running and reachable.",
      };
    }
    if (
      lower.includes("connection refused") ||
      lower.includes("not found") ||
      lower.includes("could not reach")
    ) {
      return {
        icon: <NetworkErrorIcon />,
        category: "Connection Failed",
        suggestion:
          "The server couldn't be reached. Verify the URL and that the service is running.",
      };
    }
    if (lower.includes("connection reset")) {
      return {
        icon: <NetworkErrorIcon />,
        category: "Connection Reset",
        suggestion: "The connection was interrupted. Try again or check your network.",
      };
    }
    return {
      icon: <AlertIcon />,
      category: "Error",
      suggestion: "",
    };
  };

  const doConfigureAndVerify = async (): Promise<{
    ok: boolean;
    error?: string;
  }> => {
    const trimmed = apiKey.trim();

    if (provider === "custom") {
      const urlError = validateUrl(baseUrl);
      if (urlError) {
        setBaseUrlError(urlError);
        return { ok: false, error: urlError };
      }
    }

    if (trimmed && provider !== "custom") {
      const formatError = validate(trimmed, provider);
      if (formatError) {
        return { ok: false, error: formatError };
      }
    }

    // Save config
    const result = await aiAPI.configure(
      trimmed,
      provider,
      provider === "custom" ? baseUrl.trim() : undefined,
      provider === "custom" ? modelName.trim() : undefined,
    );

    if (!result.configured) {
      return { ok: false, error: result.message || "Failed to configure AI" };
    }

    // Verify connection
    const verifyPromise = aiAPI.verify();
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error("Connection timed out. The server may be unreachable.")),
        15_000,
      ),
    );
    const verification = await Promise.race([verifyPromise, timeoutPromise]);
    if (verification.valid) {
      return { ok: true };
    }
    // Rollback on failure
    await aiAPI.removeConfigure();
    await refreshAIStatus();
    return {
      ok: false,
      error: verification.error || "Invalid API key. Please check your key and try again.",
    };
  };

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setTestResult(null);

    try {
      const result = await doConfigureAndVerify();
      if (result.ok) {
        setTestResult("success");
        // Roll back — test only, don't persist
        await aiAPI.removeConfigure();
        await refreshAIStatus();
      } else {
        setTestResult("error");
        setError(result.error || "Test failed");
      }
    } catch (err: unknown) {
      setTestResult("error");
      const detail = err instanceof Error ? err.message : "";
      setError(
        detail
          ? `Connection failed: ${detail}`
          : "Could not verify connection. Please check your settings and try again.",
      );
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setTestResult(null);

    try {
      const result = await doConfigureAndVerify();
      if (result.ok) {
        setVerified(true);
        toast.success("AI connected successfully!");
        await refreshAIStatus();
        onConfigured?.();
      } else {
        setError(result.error || "Failed to connect");
      }
    } catch (err: unknown) {
      const detail = err instanceof Error ? err.message : "";
      setError(
        detail
          ? `Connection failed: ${detail}`
          : "Could not verify connection. Please check your settings and try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const resetState = () => {
    setVerified(false);
    setTestResult(null);
    setError(null);
    setFetchModelsError(null);
  };

  return (
    <div className={styles.container}>
      {/* Compact segmented provider selector */}
      <div className={styles.field}>
        <label className={styles.label}>Provider</label>
        <div className={styles.segmentedControl}>
          {PROVIDERS.map((p) => (
            <button
              key={p.value}
              type="button"
              className={`${styles.segment} ${provider === p.value ? styles.segmentActive : ""}`}
              onClick={() => {
                setProvider(p.value);
                setError(apiKey.trim() ? validate(apiKey, p.value) : null);
                setBaseUrlError(null);
                setTestResult(null);
                if (p.value === "custom" && !baseUrl.trim()) {
                  setBaseUrl(DEFAULT_CUSTOM_BASE_URL);
                }
                resetState();
              }}
            >
              <span className={styles.segmentIcon}>{p.icon}</span>
              <span className={styles.segmentLabel}>{p.label}</span>
            </button>
          ))}
        </div>
      </div>

      {provider === "custom" ? (
        <div className={styles.customFieldsGroup}>
          <div className={styles.field}>
            <div className={styles.labelRow}>
              <label className={styles.label} htmlFor="ai-base-url">
                Base URL <span className={styles.labelHint}>(required)</span>
              </label>
              {baseUrl !== DEFAULT_CUSTOM_BASE_URL && baseUrl.trim() !== "" && (
                <button
                  type="button"
                  className={styles.resetLink}
                  onClick={() => {
                    setBaseUrl(DEFAULT_CUSTOM_BASE_URL);
                    setBaseUrlError(null);
                    resetState();
                  }}
                >
                  Reset to default
                </button>
              )}
            </div>
            <input
              id="ai-base-url"
              type="url"
              className={`${styles.inputPlain} ${!baseUrl.trim() ? styles.inputRequired : ""}`}
              placeholder={DEFAULT_CUSTOM_BASE_URL}
              value={baseUrl}
              onChange={(e) => {
                setBaseUrl(e.target.value);
                setBaseUrlError(e.target.value.trim() ? validateUrl(e.target.value) : null);
                setAvailableModels([]);
                resetState();
              }}
              onBlur={() => {
                if (baseUrl.trim()) setBaseUrlError(validateUrl(baseUrl));
              }}
            />
            {baseUrlError ? (
              <div className={styles.errorRow}>
                <AlertIcon />
                <p className={styles.errorText}>{baseUrlError}</p>
              </div>
            ) : fetchModelsError ? (
              <div className={styles.errorRow}>
                <AlertIcon />
                <p className={styles.errorText}>{fetchModelsError}</p>
              </div>
            ) : (
              <p className={styles.fieldHint}>
                Compatible with OpenAI-API compliant servers (e.g., Ollama, LM Studio).
              </p>
            )}
          </div>

          <div className={styles.field}>
            <div className={styles.labelRow}>
              <label className={styles.label} htmlFor="ai-model-name">
                Model Name <span className={styles.labelHint}>(required)</span>
              </label>
              {baseUrl.trim() && !baseUrlError && (
                <button
                  type="button"
                  className={styles.fetchModelsLink}
                  onClick={handleFetchModels}
                  disabled={fetchingModels}
                >
                  {fetchingModels
                    ? "Fetching…"
                    : availableModels.length > 0
                      ? "Refresh models"
                      : "Fetch models"}
                </button>
              )}
            </div>
            {availableModels.length > 0 ? (
              <select
                id="ai-model-name"
                className={`${styles.inputPlain} ${!modelName.trim() ? styles.inputRequired : ""}`}
                value={modelName}
                onChange={(e) => {
                  setModelName(e.target.value);
                  resetState();
                }}
              >
                {!availableModels.includes(modelName) && modelName.trim() && (
                  <option value={modelName}>{modelName}</option>
                )}
                {availableModels.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id="ai-model-name"
                type="text"
                className={`${styles.inputPlain} ${!modelName.trim() ? styles.inputRequired : ""}`}
                placeholder="e.g. llama3.2, mistral-large, gpt-4o"
                value={modelName}
                onChange={(e) => {
                  setModelName(e.target.value);
                  resetState();
                }}
              />
            )}
            {/* fetchModelsError displayed under Base URL field */}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="ai-api-key">
              API Key<span className={styles.labelHint}> (optional)</span>
            </label>
            <div className={styles.inputRow}>
              <div className={styles.inputWrapper}>
                <KeyIcon />
                <input
                  id="ai-api-key"
                  type="password"
                  className={`${styles.input} ${error ? styles.inputError : ""} ${verified ? styles.inputVerified : ""}`}
                  aria-invalid={!!error}
                  placeholder={PROVIDER_PLACEHOLDERS[provider]}
                  value={apiKey}
                  disabled={verified}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    resetState();
                    setError(e.target.value.trim() ? validate(e.target.value, provider) : null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !verified) handleSave();
                  }}
                />
              </div>
              {verified && (
                <button
                  type="button"
                  className={styles.changeButton}
                  onClick={() => {
                    resetState();
                    setApiKey("");
                  }}
                >
                  Change
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.field}>
          <label className={styles.label} htmlFor="ai-api-key">
            API Key
          </label>
          <div className={styles.inputRow}>
            <div className={styles.inputWrapper}>
              <KeyIcon />
              <input
                id="ai-api-key"
                type="password"
                className={`${styles.input} ${error ? styles.inputError : ""} ${verified ? styles.inputVerified : ""}`}
                aria-invalid={!!error}
                placeholder={PROVIDER_PLACEHOLDERS[provider]}
                value={apiKey}
                disabled={verified}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  resetState();
                  setError(e.target.value.trim() ? validate(e.target.value, provider) : null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !verified) handleSave();
                }}
              />
            </div>
            {verified && (
              <button
                type="button"
                className={styles.changeButton}
                onClick={() => {
                  resetState();
                  setApiKey("");
                }}
              >
                Change
              </button>
            )}
          </div>

          {(() => {
            const current = PROVIDERS.find((p) => p.value === provider);
            return current?.keyUrl ? (
              <p className={styles.fieldHint}>
                Don&apos;t have a key?{" "}
                <a
                  href={current.keyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.keyLink}
                >
                  Get your {current.label} API key →
                </a>
              </p>
            ) : null;
          })()}
        </div>
      )}

      {/* Connection status — shown near the action buttons for clarity */}
      {error && (
        <div className={styles.errorCard}>
          {(() => {
            const classified = classifyError(error);
            return (
              <>
                <div className={styles.errorCardHeader}>
                  {classified.icon}
                  <span className={styles.errorCardCategory}>{classified.category}</span>
                </div>
                <p className={styles.errorCardMessage}>{error}</p>
                {classified.suggestion && (
                  <p className={styles.errorCardSuggestion}>{classified.suggestion}</p>
                )}
              </>
            );
          })()}
        </div>
      )}

      {testResult === "success" && !error && (
        <div className={styles.successBanner}>
          <SuccessIcon />
          <span>Connection verified — credentials are valid.</span>
        </div>
      )}

      {!verified && (
        <div className={styles.buttonRow}>
          <button
            type="button"
            className={styles.testButton}
            disabled={!canTest}
            onClick={handleTest}
          >
            {testing ? (
              <>
                <SpinnerIcon />
                Testing…
              </>
            ) : (
              <>
                <TestIcon />
                Test Connection
              </>
            )}
          </button>
          <button
            type="button"
            className={styles.saveButton}
            disabled={!canSubmit}
            onClick={handleSave}
          >
            {saving ? (
              <>
                <SpinnerIcon />
                Connecting…
              </>
            ) : (
              <>
                <CheckCircleIcon />
                Save & Connect
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.5 8L7.2 9.7L10.5 6.3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SuccessIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M5.5 8L7.2 9.7L10.5 6.3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TestIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2 8h2m8 0h2M8 2v2m0 8v2M4.5 4.5l1.4 1.4m4.2 4.2l1.4 1.4M11.5 4.5l-1.4 1.4M5.9 9.9l-1.4 1.4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function AnthropicIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M13.827 3.52L19.29 18.48h-3.507l-1.09-2.99H9.307l-1.09 2.99H4.71L10.173 3.52h3.654ZM12 7.28l-2.07 5.68h4.14L12 7.28Z"
        fill="currentColor"
      />
    </svg>
  );
}

function OpenAIIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.998 5.998 0 0 0-3.998 2.9 6.042 6.042 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073ZM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494ZM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646ZM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872v.024Zm16.597 3.855-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667Zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66v.018ZM8.318 12.86l-2.024-1.168a.074.074 0 0 1-.038-.057V6.056a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681l-.003 6.72h.01Zm1.1-2.36 2.603-1.502 2.606 1.502v2.999l-2.597 1.502-2.612-1.502Z"
        fill="currentColor"
      />
    </svg>
  );
}

function CustomIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68 1.65 1.65 0 0 0 10 3.17V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.32 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg
      className={styles.inputIcon}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.5 1.5a3.5 3.5 0 0 0-3.22 4.87L2 11.67V14h2.33l.67-.67v-1.33h1.33L7 11.33V10h1.33l.8-.8A3.5 3.5 0 1 0 10.5 1.5Zm1 3a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"
        fill="currentColor"
      />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 5v3m0 2.5h.007M14 8A6 6 0 1 1 2 8a6 6 0 0 1 12 0Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function KeyErrorIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.5 1.5a3.5 3.5 0 0 0-3.22 4.87L2 11.67V14h2.33l.67-.67v-1.33h1.33L7 11.33V10h1.33l.8-.8A3.5 3.5 0 1 0 10.5 1.5Zm1 3a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"
        fill="currentColor"
      />
      <line
        x1="13"
        y1="3"
        x2="3"
        y2="13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TimeoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 4.5V8l2.5 1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function NetworkErrorIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2 12l3-3m6 0l3 3M5 9l3-3 3 3M8 6V3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="2"
        y1="2"
        x2="14"
        y2="14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg
      className={styles.spinner}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
      <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
